import { GetServerSideProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import dynamic from "next/dynamic";
import DefaultErrorPage from "next/error";
import Head from "next/head";
import { useRouter } from "next/router";
import { ReactElement, useEffect, useState } from "react";

import { RouteName } from "@/consts/RouteName";
import { mySubscriptionTranslations } from "@/consts/pageTranslationNamespace";
import { TableTitle } from "@/features/common/components/Table/Layout";
import { TablePlaceholder } from "@/features/common/components/placeholder/TablePlaceholder";
import { View } from "@/features/common/components/tailwind/View";
import { checkIsCorrectPath } from "@/features/common/helper/checkIsCorrectPath";
import { PaymentSettingLayout } from "@/features/section/components/paymentSetting/PaymentSettingLayout";
import { LoginRequired } from "@/features/user/components/LoginRequired";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import { getPublicConfig } from "@/helpers/getPublicConfig";

const SubscriptionTable = dynamic(
  () =>
    import(
      "@/features/subscription/components/SubscriptionTable/SubscriptionTable"
    ).then((mod) => mod.SubscriptionTable),
  {
    ssr: false,
  },
);

function MySubscription() {
  const config = getPublicConfig();
  const { t } = useTranslation([
    "profileNavigation",
    "subscriptionPlan",
    "common",
  ]);

  const { user, ready } = useUserContext();
  const router = useRouter();

  const isCorrectPath = checkIsCorrectPath(router, RouteName.MySubscription);

  useEffect(() => {
    if (ready && !user) {
      router.replace(RouteName.Home);
    }
  }, [ready, user]);

  if (!isCorrectPath) return <DefaultErrorPage statusCode={404} />;
  if (!ready)
    return (
      <View className="flex flex-col">
        <TableTitle>
          {t(`profileNavigation:subscriptionPlan.record`)}
        </TableTitle>
        <TablePlaceholder />
      </View>
    );

  return (
    <>
      <Head>
        <title>{`${t("common:pageNavigation.mySubscriptions")} | ${
          config.siteName
        }`}</title>
      </Head>

      <View className="flex flex-col">
        <TableTitle>
          {t(`profileNavigation:subscriptionPlan.record`)}
        </TableTitle>
        <SubscriptionTable showSubscribing />
      </View>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, mySubscriptionTranslations)),
    },
  };
};

MySubscription.getLayout = function getLayout(page: ReactElement) {
  return <PaymentSettingLayout>{page}</PaymentSettingLayout>;
};

export default MySubscription;
